import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Building2, Stethoscope, Pill, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SearchBar from '../components/common/SearchBar';
import ListingCard from '../components/common/ListingCard';
import ListingMap from '../components/common/ListingMap';
import { listingsAPI } from '../lib/api';

const DirectoryPage = ({ type }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const typeConfig = {
    facilities: {
      icon: Building2,
      title: 'Medical Facilities',
      description: 'Find hospitals, clinics, urgent care centers, and imaging centers near you.',
      singularType: 'facility',
      color: 'teal',
    },
    specialists: {
      icon: Stethoscope,
      title: 'Medical Specialists',
      description: 'Search for doctors and healthcare providers by specialty.',
      singularType: 'specialist',
      color: 'blue',
    },
    pharmacies: {
      icon: Pill,
      title: 'Pharmacies',
      description: 'Find local pharmacies offering prescription services and more.',
      singularType: 'pharmacy',
      color: 'rose',
    },
  };

  const config = typeConfig[type] || typeConfig.facilities;
  const Icon = config.icon;

  useEffect(() => {
    loadListings();
  }, [type]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const res = await listingsAPI.getAll({ listing_type: config.singularType, limit: 50 });
      setListings(res.data);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`${type}-page`}>
      {/* Hero */}
      <section className={`bg-gradient-to-br from-${config.color}-50 to-white py-12 md:py-16 border-b`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 rounded-xl bg-${config.color}-100 flex items-center justify-center`}>
              <Icon className={`w-7 h-7 text-${config.color}-700`} />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
                {config.title}
              </h1>
              <p className="text-gray-600">{listings.length} listings available</p>
            </div>
          </div>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl">
            {config.description}
          </p>
          <SearchBar />
        </div>
      </section>

      {/* Listings */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl font-semibold text-gray-900">
              Browse {config.title}
            </h2>
            <Link to={`/search?type=${config.singularType}`}>
              <Button variant="outline">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Map */}
          <div className="mb-8">
            <ListingMap listings={listings} className="h-[400px]" />
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-80 skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DirectoryPage;
